import { useBreadcrumbs } from '@features/layout/hooks/useBreadcrumbs';
import { useOfferProperties } from '@features/offers/hooks/useOfferProperties';
import { yupResolver } from '@hookform/resolvers/yup';
import { ToastMessage } from '@molecules';
import { useQuery } from '@tanstack/react-query';
import { formatCurrencyBRL } from '@utils/currency.util';
import { Modal } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';

import { productApi } from '../api/productApi';
import { ProductFormData } from '../interfaces/productInterface';
import { formProductEditFactory } from '../utils/formProductEditFactory';
import { formProductFactory } from '../utils/formProductFactory';
import { useCategoriesProduct } from './useCategoriesProduct';
import { useProfileProduct } from './useProfileProduct';
import { useProviderProduct } from './useProviderProduct';

export function useFormProduct() {
  const [isLoading, setIsLoading] = useState(false);
  const [showProductLoadErrorModal, setShowProductLoadErrorModal] =
    useState(false);

  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();

  const isViewingMode = useMemo(
    () => searchParams.get('isViewingMode') === 'true',
    [searchParams]
  );

  const validationSchema = yup.object({
    name: yup
      .string()
      .required('Nome do produto é obrigatório')
      .test(
        'no-spaces-only',
        'O nome do produto não pode conter apenas espaços em branco',
        value => {
          if (typeof value !== 'string') return false;
          return value.trim().length > 0;
        }
      ),
    releaseProfileId: yup
      .string()
      .required('Profile de liberação é obrigatório'),
    warrantyDays: yup
      .number()
      .transform((value, originalValue) => {
        if (typeof originalValue !== 'string') return value;

        const normalizedValue = originalValue.trim();

        if (!normalizedValue) return value;

        return Number(normalizedValue.replace(',', '.'));
      })
      .required('Garantia é um número obrigatório')
      .min(7, 'A garantia deve ser de no mínimo 7 dias')
      .test(
        'no-points',
        'A garantia não pode conter pontos e nem vírgulas',
        function (value) {
          if (value === undefined) return true;

          if (typeof this.originalValue !== 'string') return true;

          return !this.originalValue.match(/[.,]/);
        }
      )
      .test('is-integer', 'A garantia deve ser um número inteiro', value =>
        Number.isInteger(value || 0)
      ),
    offers: yup
      .array()
      .of(
        yup.object({
          fullPrice: yup
            .string()
            .required('Valor da oferta é obrigatório')
            .test(
              'is-positive',
              'O valor da oferta deve ser maior que zero',
              value => {
                const numericValue = value
                  ? value.includes(',')
                    ? parseFloat(value.replace(/\./g, '').replace(',', '.'))
                    : parseFloat(value)
                  : 0;
                return numericValue > 0;
              }
            ),
          acquisitionId: yup.string().required('Aquisição é obrigatório'),
          seasonalityId: yup.string().required('Sazonalidade é obrigatória'),
          typeId: yup.string().required('Tipo de venda é obrigatório'),
          isFallbackOffer: yup.boolean().optional(),
        })
      )
      .required('É necessário adicionar pelo menos uma oferta')
      .min(1, 'É necessário adicionar pelo menos uma oferta')
      .test(
        'unique-tas-combination',
        'Combinação de tipo de oferta, aquisição e sazonalidade já existe',
        function (offers) {
          if (!offers || offers.length === 0) return true;

          const combinations = new Set<string>();
          const duplicateIndices: number[] = [];

          offers.forEach((offer, index) => {
            const combination = `${offer.acquisitionId}-${offer.seasonalityId}-${offer.typeId}`;

            if (combinations.has(combination)) {
              duplicateIndices.push(index);
            } else {
              combinations.add(combination);
            }
          });

          if (duplicateIndices.length > 0) {
            // Store duplicate indices for UI highlighting
            return this.createError({
              message:
                'Combinação de tipo de oferta, aquisição e sazonalidade já existe',
              path: 'offers',
              params: { duplicateIndices },
            });
          }

          return true;
        }
      ),
    providerId: yup.string().required('Provedor é obrigatório'),
    categories: yup
      .array()
      .of(yup.string().required())
      .min(1, 'É necessário selecionar pelo menos uma categoria')
      .required('É necessário selecionar pelo menos uma categoria'),
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    formState,
  } = useForm<ProductFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      name: '',
      releaseProfileId: '',
      providerId: '',
      categories: [],
      isPrincipal: false,
      isOnlyFallback: false,
      isMonthly: false,
      offers: [
        {
          fullPrice: '',
          acquisitionId: '',
          seasonalityId: '',
          typeId: '',
          isFallbackOffer: true,
        },
      ],
    },
  });

  const { profilesData, isLoadingProfiles } = useProfileProduct();
  const { providersData, isLoadingProviders } = useProviderProduct();
  const {
    offerProperties,
    rulesTASRelational,
    isLoading: isLoadingOfferProperties,
    isError: isErrorOfferProperties,
  } = useOfferProperties();

  const { categoriesProductData, isLoadingCategories } = useCategoriesProduct();
  const [isOnlyFallback, isMonthly] = watch(['isOnlyFallback', 'isMonthly']);

  const isFormValid = useMemo(() => {
    return formState.isValid;
  }, [formState.isValid]);

  const handleBack = () => {
    navigate('/products');
  };

  const handleFinishCreateProduct = (productId: number) => {
    navigate(`/products/${productId}/offers`);
  };

  const {
    data: currentProductData,
    isLoading: isLoadingProduct,
    isError: isErrorProduct,
  } = useQuery({
    queryKey: ['editProduct', productId],
    queryFn: async () => {
      if (!productId) return null;
      return await productApi.getProductById(Number(productId));
    },
    enabled: !!productId,
  });

  useEffect(() => {
    if (!isErrorProduct || !productId) return;

    setShowProductLoadErrorModal(true);
  }, [isErrorProduct, productId]);

  const handleProductLoadErrorConfirm = () => {
    setShowProductLoadErrorModal(false);
    navigate(-1);
  };

  const titlePage = useMemo(() => {
    if (isViewingMode)
      return currentProductData?.publicName || 'Visualizar Produto';

    return productId
      ? currentProductData?.publicName || 'Editar Produto'
      : 'Criar Produto';
  }, [productId, isViewingMode, currentProductData]);

  const updateBreadcrumbs = useBreadcrumbs([]);

  useEffect(() => {
    if (!!productId && !currentProductData) return;

    updateBreadcrumbs([
      {
        label: 'Produtos',
        route: '/products',
      },
      {
        label: titlePage,
      },
    ]);
  }, [titlePage]);

  useEffect(() => {
    if (!currentProductData) return;

    reset({
      name: currentProductData.publicName || currentProductData.name,
      releaseProfileId: currentProductData.profileId
        ? String(currentProductData.profileId)
        : '',
      providerId: String(currentProductData.providerId),
      categories:
        currentProductData.categories?.map(categoryId => String(categoryId)) ||
        [],
      warrantyDays: currentProductData.warrantyDays,
      offers:
        currentProductData.offers?.map(offer => ({
          fullPrice: formatCurrencyBRL(offer.fullPrice),
          acquisitionId: String(offer.acquisitionId),
          seasonalityId: String(offer.seasonalityId),
          typeId: String(offer.typeId),
          isFallbackOffer: offer.isFallbackOffer,
        })) || [],
      isPrincipal: currentProductData.isPrincipal || false,
      isOnlyFallback: currentProductData.isDirectFallback,
      isMonthly: currentProductData.subscriptionType === 'monthly',
    });
  }, [currentProductData, reset]);

  // Regra: isOnlyFallback e isMonthly não podem ser true ao mesmo tempo
  useEffect(() => {
    if (isOnlyFallback && isMonthly) {
      setValue('isMonthly', false);
      Modal.warning({
        title: 'Campo desabilitado automaticamente',
        content:
          'O campo "Produto mensal" foi desabilitado pois não pode estar ativo junto com "Apenas cobrança recorrente na primeira compra".',
        centered: true,
      });
    }
  }, [isOnlyFallback]);

  useEffect(() => {
    if (isMonthly && isOnlyFallback) {
      setValue('isOnlyFallback', false);
      Modal.warning({
        title: 'Campo desabilitado automaticamente',
        content:
          'O campo "Apenas cobrança recorrente na primeira compra" foi desabilitado pois não pode estar ativo junto com "Produto mensal".',
        centered: true,
      });
    }
  }, [isMonthly]);

  const handleCreateProduct = async (data: ProductFormData) => {
    if (!data || isViewingMode) return;
    try {
      setIsLoading(true);
      if (productId) {
        if (!currentProductData) {
          throw new Error('Dados do produto nao carregados para edição');
        }

        const productUpdatePayload = formProductEditFactory({
          formData: data,
        });

        await productApi.updateProduct(Number(productId), productUpdatePayload);

        ToastMessage({
          content: 'Produto editado com sucesso!',
          type: 'success',
        });
        handleBack();
      } else {
        const productPayload = formProductFactory(data);

        const createdProduct = await productApi.createProduct(productPayload);
        handleFinishCreateProduct(createdProduct.id);
      }
    } catch (error) {
      console.error('error', error);

      ToastMessage({
        content: productId
          ? 'Não foi possível editar o produto, entre em contato com o suporte!'
          : 'Não foi possível criar o produto, entre em contato com o suporte!',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleErrorsProduct = (errors: any) => {
    if (isViewingMode) return;
    // Rola o scroll para o topo
    window.scrollTo({ top: 0, behavior: 'smooth' });

    console.error('errors', errors);

    ToastMessage({
      content: 'Por favor, verifique os campos obrigatórios.',
      type: 'warning',
    });
  };

  const hasLoadingPage = useMemo(() => {
    return (
      isLoadingProfiles ||
      isLoadingProviders ||
      isLoadingCategories ||
      isLoadingOfferProperties ||
      (isViewingMode && isLoadingProduct)
    );
  }, [
    isLoadingProfiles,
    isLoadingProduct,
    isLoadingProviders,
    isLoadingCategories,
    isLoadingOfferProperties,
    isViewingMode,
  ]);

  const hasExistingOffers = !!currentProductData?.offers?.length;
  const isEditMode = !!productId && !isViewingMode;

  return {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    setValue,
    profilesData,
    isLoadingProfiles,
    isLoading,
    titlePage,
    isViewingMode,
    currentProductData,
    isLoadingProduct,
    handleCreateProduct,
    handleErrorsProduct,
    productId,
    handleBack,
    isFormValid,
    isErrorProduct,
    providersData,
    categoriesProductData,
    isLoadingCategories,
    isLoadingProviders,
    hasLoadingPage,
    showProductLoadErrorModal,
    handleProductLoadErrorConfirm,
    offerProperties,
    isLoadingOfferProperties,
    isErrorOfferProperties,
    formState,
    hasExistingOffers,
    isEditMode,
    rulesTASRelational,
  };
}
